import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, FileText, User, Building2, Clock, Loader2, Activity, Mail, Phone, Link } from 'lucide-react';
import './AdminActivityLogs.css';

const AdminActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem('adminToken'); // Get the token!
        
        // Use the EXACT matching URL with the Auth header
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/resume-downloads`, {
          headers: {
            Authorization: `Bearer ${token}` 
          }
        });
        
        setLogs(response.data);
      } catch (error) {
        console.error("Failed to fetch download logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (isLoading) {
    return (
      <div className="aal-loading">
        <Loader2 className="aal-spin" size={36} />
        <p>Loading activity logs...</p>
      </div>
    );
  }

  return (
    <div className="aal-container">
      <div className="aal-header">
        <div className="aal-title-wrapper">
          <div className="aal-title-icon">
            <Activity size={24} />
          </div>
          <div>
            <h2 className="aal-title">System Activity Logs</h2>
            <p className="aal-subtitle">Track customer resume downloads and interactions.</p>
          </div>
        </div>
        <div className="aal-badge">
          {logs.length} Total Downloads
        </div>
      </div>

      <div className="aal-card">
        <div className="aal-table-wrapper">
          <table className="aal-table">
            <thead>
              <tr>
                <th><Clock size={14} className="aal-th-icon" /> Timestamp</th>
                <th><Building2 size={14} className="aal-th-icon" /> Customer Details</th>
                <th><FileText size={14} className="aal-th-icon" /> Target Job</th>
                <th><User size={14} className="aal-th-icon" /> Candidate Details</th>
                <th><Link size={14} className="aal-th-icon" /> Action</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="aal-empty">
                    <Activity size={40} className="aal-empty-icon" />
                    <p>No resume downloads logged yet.</p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id}>
                    {/* Column 1: Time */}
                    <td className="aal-td-time">
                      {formatDate(log.createdAt)}
                    </td>
                    
                    {/* Column 2: Customer */}
                    <td>
                      <div className="aal-primary-text">{log.customerName}</div>
                      <div className="aal-secondary-text" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Mail size={12} /> {log.customerEmail}
                      </div>
                    </td>
                    
                    {/* Column 3: Job */}
                    <td>
                      <div className="aal-primary-text">{log.jobName}</div>
                      <div className="aal-secondary-text">{log.jobLocation}</div>
                    </td>
                    
                    {/* Column 4: Candidate (NOW SHOWING ALL DETAILS) */}
                    <td>
                      <div className="aal-primary-text">{log.candidateName}</div>
                      <div className="aal-secondary-text" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        <Mail size={12} /> {log.candidateEmail || 'No Email Provided'}
                      </div>
                      <div className="aal-secondary-text" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Phone size={12} /> {log.candidatePhone || 'No Phone Provided'}
                      </div>
                    </td>
                    
                    {/* Column 5: Resume Download */}
                    <td>
                      {log.resumeUrl ? (
                        <a 
                          href={log.resumeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="aal-download-link"
                        >
                          <Download size={13} /> Download
                        </a>
                      ) : (
                        <span className="aal-secondary-text">No File Linked</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminActivityLogs;